import { type MouseEvent, useCallback, useEffect, useState } from "react";
import { CarConfigStep } from "../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { useCarConfigDispatch, useCarConfigSelector } from "../redux/store";
import { navigateToStep } from "../utils/navigationUtils";
import { type StepsType, trackTopNavigationClick } from "../utils/tracking";
import { type SectionType, setActiveSection, setNavigationStep } from "../redux/slices/CarConfigNavigationSlice";
import {
    getCarConfigSessionStorage,
    getSessionStorageParams,
    setActiveSectionToStorage,
} from "../utils/sessionStorage";
import { scrollToTarget } from "../../../../common-deprecated/utils/scrollUtils";

type EventType = MouseEvent<HTMLAnchorElement | HTMLButtonElement>;

type useAphNavigationType = (
    target: CarConfigStep,
    subTarget?: StepsType,
    scrollPosition?: ScrollLogicalPosition,
) => (event: EventType) => void;

export const useAphNavigation = (): useAphNavigationType => {
    const dispatch = useCarConfigDispatch();
    const country = useCarConfigSelector((state) => state.commonSettings.country);
    const language = useCarConfigSelector((state) => state.commonSettings.language);
    const car = useCarConfigSelector((state) => state.car);
    const isStandalone = useCarConfigSelector((state) => state.commonSettings.isStandalone);
    const loadedStepUrls = useCarConfigSelector((state) => state.carConfigSettings.loadedStepUrls);
    const basket = useCarConfigSelector((state) => state.basket);
    const activeSection = useCarConfigSelector((state) => state.carConfigNavigation.activeSection);
    const activeStep = useCarConfigSelector((state) => state.carConfigNavigation.step);
    const sessionStorageParams = useCarConfigSelector(getSessionStorageParams);
    const disableSpaNav = useCarConfigSelector((state) => !!state.carConfigSettings.xcaModules);

    const [pendingScroll, setPendingScroll] = useState<{
        target: StepsType;
        scrollPosition?: ScrollLogicalPosition;
    } | null>(null);

    // Scroll to target after the step has been updated to prevent not scrolling to the desired scroll position
    useEffect(() => {
        if (pendingScroll) {
            requestAnimationFrame(() => {
                scrollToTarget(pendingScroll.target, pendingScroll.scrollPosition);
                setPendingScroll(null);
            });
        }
    }, [activeStep]);

    return useCallback(
        (target: CarConfigStep, subTarget?: StepsType, scrollPosition?: ScrollLogicalPosition) =>
            (event: EventType) => {
                event.preventDefault();
                if (activeSection) {
                    trackTopNavigationClick(target, activeSection);
                }
                if (target !== activeStep) {
                    if (subTarget) {
                        dispatch(setActiveSection(subTarget as SectionType));

                        // Important as the IntersectionObserver would overwrite the current active section with the one from the session storage.
                        setActiveSectionToStorage(
                            sessionStorageParams,
                            subTarget,
                            CarConfigStep.Configure,
                            car.model.id,
                        );
                        setPendingScroll({ target: subTarget, scrollPosition });
                    }

                    navigateToStep(country, language, loadedStepUrls, target, car, basket, isStandalone, disableSpaNav);
                    dispatch(setNavigationStep(target));

                    const storage = getCarConfigSessionStorage(sessionStorageParams);
                    const activeSections = storage?.activeSections ? storage?.activeSections[car.model.id] : undefined;

                    // Show header on step switch and scroll to subtarget/top
                    if (
                        target === CarConfigStep.Specs ||
                        (target === CarConfigStep.Highlights && !activeSections?.highlights) ||
                        (target === CarConfigStep.Configure && !activeSections?.configure)
                    ) {
                        window.scrollTo({ top: 0, behavior: "instant" });
                    }
                } else if (subTarget) {
                    scrollToTarget(subTarget, scrollPosition);
                }
            },
        [activeSection, activeStep, loadedStepUrls, car, basket, isStandalone, dispatch, sessionStorageParams],
    );
};
